import { Popover } from '@mui/material';
import moment from 'moment-timezone';
import { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { twMerge } from 'tailwind-merge';

import CalendarIcon from '../../../assets/svg/CalendarIcon';
import CrossIcon from '../../../assets/svg/CrossIcon';
import { DATE_TIME_FORMAT } from '../../../constants';
import {
  getFormattedDateInMomentDefaultTZ,
  getNowInDefaultTimezoneAsDate,
} from '../../../helpers';
import { Button } from '../../Shared';

import './react-date-picker.css';

const DEFAULT_TITLE = 'Today';

const isSameDate = (date, comparedDate) =>
  moment(date).isSame(comparedDate, 'day');

const getTitle = selectedDate => {
  const isToday = isSameDate(selectedDate, getNowInDefaultTimezoneAsDate());

  if (isToday) return DEFAULT_TITLE;

  return `${getFormattedDateInMomentDefaultTZ(selectedDate).format(DATE_TIME_FORMAT.MMM_D_YYYY)}`;
};

export const DateFilter = ({ selectedDate, onChangeSelectedDate }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currSelectedDate, setCurrSelectedDate] = useState(selectedDate);

  const onChangeCurrSelectedDate = nextSelectedDate => {
    setCurrSelectedDate(nextSelectedDate);
  };

  const onChange = () => {
    onChangeSelectedDate(prev => {
      return {
        ...prev,
        selectedDate: currSelectedDate,
      };
    });
    onClosePopover();
  };

  const reset = () => {
    onChangeSelectedDate(prev => {
      return {
        ...prev,
        selectedDate: getNowInDefaultTimezoneAsDate(),
      };
    });
  };

  const onOpenPopover = e => {
    if (e.target.localName !== 'button') return;
    setCurrSelectedDate(selectedDate);
    setAnchorEl(e.currentTarget);
  };

  const onClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const title = getTitle(selectedDate);
  const isToday = title === DEFAULT_TITLE;
  const isButtonDoneDisabled = isSameDate(selectedDate, currSelectedDate);

  return (
    <>
      <button
        className={twMerge(
          'flex items-center gap-2 rounded-full py-[11px] pl-5 pr-6 text-brand-900 border border-brand-900 font-medium text-[16px]/[24px] focus-visible:outline-none',
          !isToday && 'bg-brand-900 text-white',
        )}
        onClick={onOpenPopover}
      >
        <CalendarIcon
          className={twMerge(
            'w-4 h-4 text-brand-500',
            !isToday && 'text-brand-300',
          )}
        />
        {title}
        {!isToday && (
          <div onClick={reset}>
            <CrossIcon className="w-4 h-4 text-white" />
          </div>
        )}
      </button>
      <Popover
        open={open}
        slotProps={{
          paper: {
            className:
              'leading-[0] !rounded-xl mt-2 !shadow-[0_4px_20px_0_rgba(17,17,58,0.08)] bg-white !overflow-auto',
          },
        }}
        anchorEl={anchorEl}
        onClose={onClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <ReactDatePicker
          selected={currSelectedDate}
          onChange={onChangeCurrSelectedDate}
          inline
          calendarClassName="datepicker"
        />
        <div className="px-5 py-3 border-t border-t-base-200 w-[340px]">
          <Button
            title="Done"
            onClick={onChange}
            disabled={isButtonDoneDisabled}
            className="ml-auto"
          />
        </div>
      </Popover>
    </>
  );
};
